import { useQuery } from 'react-query';

import { ExportStatus } from '../../../../backend/src/types/types';
import { QueryEntity } from '../types';
import { useFetcher } from '../useGetFetchConfig';

export interface ExportTerminalStatus {
  status: ExportStatus;
  error?: string;
}

interface UseGetExportStatusArgs {
  /** Full status endpoint URL (page-local — caller appends `?exportId=...`). */
  statusEndpoint: string | undefined;
  exportId: string | null;
  enabled: boolean;
}

const POLL_INTERVAL_MS = 10_000;

/**
 * Polls the persisted terminal-status endpoint for an async export. Used by the
 * progress modal as a fallback when the SSE channel fails — returns `null`
 * while the job is still running, otherwise `{status, error?}`.
 */
export const useGetExportStatus = ({ statusEndpoint, exportId, enabled }: UseGetExportStatusArgs) => {
  const fetcher = useFetcher();
  const isEnabled = enabled && !!statusEndpoint && !!exportId;

  return useQuery<ExportTerminalStatus | null>(
    [QueryEntity.ExportStatus, { statusEndpoint, exportId }],
    async () => {
      // Treat any failure (non-2xx, empty or malformed body) as "still pending" so
      // transient backend errors don't spam the console. The next tick will try again.
      try {
        return await fetcher<ExportTerminalStatus | null>(`${statusEndpoint}?exportId=${exportId}`);
      } catch {
        return null;
      }
    },
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchInterval: isEnabled ? POLL_INTERVAL_MS : false,
      // Buffering proxies can swallow SSE entirely, so this poll is the
      // delivery guarantee and must keep running in hidden tabs.
      refetchIntervalInBackground: true,
      retry: false,
    },
  );
};
